import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Separator,
} from "@beweegbaas/ui";
import { createFileRoute, notFound, useNavigate, useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { z } from "zod/v4";
import { DataTableActions, type DataTableAction } from "#/components/data-table-actions";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { TicketMessageThread } from "#/modules/tickets/components/ticket-message-thread";
import { TicketComposer } from "#/modules/tickets/components/ticket-composer";
import { TicketMessageEditDrawer } from "#/modules/tickets/components/ticket-message-edit-drawer";
import { TicketStatusBadge } from "#/modules/tickets/components/ticket-status-badge";
import { formatDateTime } from "#/modules/util.helpers/format";
import {
  addAdminTicketMessageFn,
  assignTicketAdmin,
  closeTicketAdmin,
  deleteAdminTicketMessageFn,
  deleteTicketAdmin,
  editAdminTicketMessageFn,
  getAdminTicketDetail,
  getTicketAssigneesAdmin,
  markAdminTicketReadFn,
  reopenTicketAdmin,
  unassignTicketAdmin,
  uploadAdminTicketFileFn,
} from "#/modules/tickets/tickets-admin.functions";
import { useMessageEditing } from "#/modules/tickets/use-message-editing";
import { useTicketEvents } from "#/modules/tickets/use-ticket-events";
import { buildLocaleHref, useLocaleParams } from "#/modules/util.helpers/route-href";
import { m } from "#/paraglide/messages";
import { genericFormError } from "#/modules/util.errors/translate";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/admin/tickets/drawer/$id",
)({
  loader: async ({ params }) => {
    // The id comes straight from the URL: a mangled link is a ticket that does not exist.
    if (!z.uuid().safeParse(params.id).success) throw notFound();
    const [ticket, assignees] = await Promise.all([
      getAdminTicketDetail({ data: { id: params.id } }),
      getTicketAssigneesAdmin(),
    ]);
    return { ticket, assignees };
  },
  component: TicketAdminDetail,
  errorComponent: TicketAdminDetailError,
  notFoundComponent: TicketAdminDetailNotFound,
});

function TicketAdminDetailNotFound() {
  return (
    <section className="flex flex-col gap-4 px-6 pt-10 pb-10">
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{m["tickets.detail.not_found_title"]()}</EmptyTitle>
          <EmptyDescription>{m["tickets.detail.not_found_description"]()}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    </section>
  );
}

function TicketAdminDetailError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

function CloseReopenButton({ ticketId, status }: { ticketId: string; status: "Open" | "Closed" }) {
  const router = useRouter();
  const closeTicket = useServerFn(closeTicketAdmin);
  const reopenTicket = useServerFn(reopenTicketAdmin);
  const [isPending, setIsPending] = useState(false);

  const handleClick = async () => {
    setIsPending(true);
    try {
      if (status === "Open") {
        await closeTicket({ data: { id: ticketId } });
      } else {
        await reopenTicket({ data: { id: ticketId } });
      }
      await router.invalidate();
    } catch {
      // toastErrorMiddleware already surfaced the error.
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Button variant="secondary" size="sm" isPending={isPending} onClick={() => void handleClick()}>
      {status === "Open" ? (
        <>
          <IconMaterialSymbolsLockOutline />
          {m["app.actions.close"]()}
        </>
      ) : (
        <>
          <IconMaterialSymbolsLockOpenOutline />
          {m["admin.tickets.detail.reopen_button"]()}
        </>
      )}
    </Button>
  );
}

// The Select needs a non-empty value for the release option; no admin userId can collide with it.
const UNASSIGNED = "unassigned";

function AssignSelect({
  ticketId,
  assignedAdminUserId,
  assignees,
}: {
  ticketId: string;
  assignedAdminUserId: string | null | undefined;
  assignees: { userId: string; name: string }[];
}) {
  const router = useRouter();
  const assignTicket = useServerFn(assignTicketAdmin);
  const unassignTicket = useServerFn(unassignTicketAdmin);
  const [isPending, setIsPending] = useState(false);

  const value = assignedAdminUserId ?? UNASSIGNED;

  const handleChange = async (next: string | null) => {
    if (!next || next === value) return;
    setIsPending(true);
    try {
      if (next === UNASSIGNED) {
        await unassignTicket({ data: { id: ticketId } });
      } else {
        await assignTicket({ data: { id: ticketId, userId: next } });
      }
      await router.invalidate();
    } catch {
      // toastErrorMiddleware already surfaced the error.
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Select value={value} onValueChange={(next: string | null) => void handleChange(next)}>
      <SelectTrigger
        size="sm"
        className="w-auto"
        disabled={isPending}
        aria-label={m["admin.tickets.detail.assign_label"]()}
        data-testid="admin-ticket-assign-select"
      >
        <IconMaterialSymbolsPersonOutline />
        <SelectValue>
          {assignees.find((a) => a.userId === assignedAdminUserId)?.name ??
            m["admin.tickets.detail.assign_label"]()}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {assignees.map((assignee) => (
            <SelectItem key={assignee.userId} value={assignee.userId}>
              {assignee.name}
            </SelectItem>
          ))}
        </SelectGroup>
        <SelectSeparator />
        <SelectGroup>
          <SelectItem value={UNASSIGNED}>{m["admin.tickets.detail.release_option"]()}</SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function TicketAdminDetail() {
  const { ticket, assignees } = Route.useLoaderData();
  const router = useRouter();
  const navigate = useNavigate();
  const localeParams = useLocaleParams();
  const markRead = useServerFn(markAdminTicketReadFn);
  const addMessage = useServerFn(addAdminTicketMessageFn);
  const uploadFile = useServerFn(uploadAdminTicketFileFn);
  const deleteTicket = useServerFn(deleteTicketAdmin);
  const editMessage = useServerFn(editAdminTicketMessageFn);
  const deleteMessage = useServerFn(deleteAdminTicketMessageFn);
  const marked = useRef<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const editing = useMessageEditing(ticket.messages);

  const markTicketRead = useCallback(async () => {
    await markRead({ data: { id: ticket.id } });
  }, [markRead, ticket.id]);

  // Mirrors the user-side "mark read on open" pattern: fire once per ticket, then refresh so the
  // admin sidebar's unread badge and this ticket's row dot clear without a full reload.
  useEffect(() => {
    if (marked.current === ticket.id) return;
    marked.current = ticket.id;
    void markTicketRead().then(() => router.invalidate());
  }, [ticket.id, markTicketRead, router]);

  useTicketEvents(ticket.id, "admin", markTicketRead);

  // Open on the latest message and the reply box, like a chat. Layout effect so the drawer never
  // paints at the top first.
  useLayoutEffect(() => {
    sectionRef.current?.scrollIntoView({ block: "end" });
  }, [ticket.id]);

  const uploadTicketFile = async (formData: FormData) => {
    const result = await uploadFile({ data: formData });
    if (!result) return genericFormError("UploadResultMissing");
    if ("formError" in result) return result;
    return { file: result.file };
  };

  const deleteActions: DataTableAction[] = [
    {
      key: "delete",
      label: m["common.actions.remove"](),
      icon: <IconMaterialSymbolsDeleteOutline />,
      destructive: true,
      confirm: {
        title: m["admin.tickets.detail.delete_confirm_title"](),
        message: m["admin.tickets.detail.delete_confirm_message"](),
      },
      onClick: async () => {
        await deleteTicket({ data: { id: ticket.id } });
        // Leave the drawer BEFORE invalidating — the deleted ticket's own detail loader would
        // refetch into TicketNotFound otherwise. The invalidate itself is required: the overview
        // is this route's parent and stays mounted, so closing the drawer alone never re-runs
        // its loader and the deleted row would linger in the table.
        await navigate({
          from: Route.fullPath,
          to: "/$locale/{-$schoolyear}/admin/tickets",
          params: (prev) => prev,
          search: (prev) => prev,
          viewTransition: true,
        });
        await router.invalidate();
      },
    },
  ];

  return (
    <section ref={sectionRef} className="flex flex-col gap-4 px-6 pt-10 pb-10">
      <header className="flex flex-col gap-2 pr-8">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h3 className="m-0">
            #{ticket.ticketNumber} {ticket.subject}
          </h3>
          <TicketStatusBadge status={ticket.status} />
        </div>
        <p className="text-muted-foreground m-0 text-sm">
          {ticket.createdByName} &middot; {ticket.createdByEmail} &middot;{" "}
          {formatDateTime(ticket.createdAt)}
          {ticket.assignedAdminName && (
            <>
              {" "}
              &middot; {m["admin.tickets.detail.assigned_to"]({ name: ticket.assignedAdminName })}
            </>
          )}
        </p>
        {/* One toolbar for every action — close/reopen, (un)assign, delete — instead of
            scattering them around the header. */}
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <CloseReopenButton ticketId={ticket.id} status={ticket.status} />
          <AssignSelect
            ticketId={ticket.id}
            assignedAdminUserId={ticket.assignedAdminUserId}
            assignees={assignees}
          />
          <DataTableActions
            actions={deleteActions}
            aria-label={m["admin.tickets.detail.actions"]()}
          />
        </div>
      </header>

      <Separator />

      <TicketMessageThread
        messages={ticket.messages}
        viewerRole="Admin"
        getAttachmentUrl={(fileId) =>
          buildLocaleHref(`/api/admin/tickets/${ticket.id}/files/${fileId}`, localeParams)
        }
        editingId={editing.open ? editing.message?.id : undefined}
        onEdit={editing.startEditing}
        onDelete={async (message) => {
          await deleteMessage({ data: { id: ticket.id, messageId: message.id } });
          await router.invalidate();
        }}
      />

      <TicketComposer
        placeholder={m["admin.tickets.detail.reply_placeholder"]()}
        onArrowUpWhenEmpty={editing.editLast}
        uploadFile={uploadTicketFile}
        onSubmit={(data) => addMessage({ data: { id: ticket.id, ...data } })}
        onSuccess={() => {
          void router.invalidate();
        }}
      />

      <TicketMessageEditDrawer
        editing={editing}
        uploadFile={uploadTicketFile}
        onSave={(messageId, data) => editMessage({ data: { id: ticket.id, messageId, ...data } })}
        onSaved={() => {
          void router.invalidate();
        }}
      />
    </section>
  );
}
